import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';

import UpdateButton from '../../Template/UpdateButton';
import EventSelector from '../../../components/EventSelector/Modal';
import EventSelectorButton from '../../../components/EventSelector/Button';
import Info from '../../../components/Info';
import ModelPrompts from './ModelPrompts';
import ImageUpload from './ImageUpload';
import PriorityOrder from './PriorityOrder';

import style from './style.module.scss';

import { updateEventsGeneric } from '../../../helpers/api';
import { updateEvent as updateEventInStore } from '../../../store/actions/events';
import { displaySuccessModal } from '../../../helpers/swal';
import { fetchEvent } from '../../Template/api';
import { useSelfieCamConfig } from '../hooks';

const MODEL_KEYS = [
  'google-gemini-3-pro-image-preview',
  'google-gemini-3-1-flash-image-preview',
  'replicate-google-gemini-3-pro-image-preview',
  'replicate-google-gemini-3-1-flash-image-preview',
  'replicate-bytedance-seedream-5-lite',
];

const buildPriorityOrder = (priority) => {
  const order = [...(priority || [])];
  MODEL_KEYS.forEach((k) => {
    if (!order.includes(k)) order.push(k);
  });
  return order.map((key) => ({ key, enabled: (priority || []).includes(key) }));
};

const DEFAULT_CONCEPT = {
  title: '',
  models: {
    'google-gemini-3-pro-image-preview': '',
    'google-gemini-3-1-flash-image-preview': '',
    'replicate-google-gemini-3-pro-image-preview': '',
    'replicate-google-gemini-3-1-flash-image-preview': '',
    'replicate-bytedance-seedream-5-lite': '',
  },
  priority: [],
  imageInputs: [],
};

const mergeConceptState = (raw) => ({
  ...DEFAULT_CONCEPT,
  ...raw,
  models: { ...DEFAULT_CONCEPT.models, ...(raw?.models || {}) },
});

const AiTemplateConcept = ({
  slugDate,
  template,
  allEvents,
}) => {
  const dispatch = useDispatch();
  const { config, isLoading } = useSelfieCamConfig(slugDate);
  const [conceptData, setConceptData] = useState(DEFAULT_CONCEPT);
  const [priorityOrder, setPriorityOrder] = useState(() => buildPriorityOrder(
    DEFAULT_CONCEPT.priority,
  ));

  useEffect(() => {
    if (!slugDate) {
      setConceptData(DEFAULT_CONCEPT);
      setPriorityOrder(buildPriorityOrder(DEFAULT_CONCEPT.priority));
      return;
    }

    const raw = config?.concept;
    if (raw && typeof raw === 'object') {
      const merged = mergeConceptState(raw);
      setConceptData(merged);
      setPriorityOrder(buildPriorityOrder(merged.priority));
      return;
    }

    setConceptData(DEFAULT_CONCEPT);
    setPriorityOrder(buildPriorityOrder(DEFAULT_CONCEPT.priority));
  }, [slugDate, config]);

  const handleTitleChange = (value) => {
    setConceptData((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const updateEventHandler = async (eventIds) => {
    if (!eventIds?.length || !conceptData) return;
    const priority = priorityOrder.filter((p) => p.enabled).map((p) => p.key);
    const payload = { ...conceptData, priority, imageInputs: conceptData.imageInputs ?? [] };
    await updateEventsGeneric(eventIds, { 'selfiecam.concept': payload });

    const slugDates = eventIds
      .map((id) => (template?._id === id ? template : allEvents.find((e) => e._id === id)))
      .filter(Boolean)
      .map((e) => e.slugDate)
      .filter(Boolean);
    await Promise.all(slugDates.map(async (sd) => {
      try {
        const fresh = await fetchEvent(sd);
        dispatch(updateEventInStore(fresh));
      } catch (err) {
        console.error(err);
      }
    }));

    displaySuccessModal({ text: 'AI Concept updated successfully!' });
  };

  return (
    <div className={classNames('d-flex', 'flex-column', 'flex-grow-1')}>
      <div className="pageHeader">
        <h4>
          AI Concept
          <Info
            content={(
              <>
                Expected output
                <ul className={style.tooltipList}>
                  <li>Aspect ratio: 4:5</li>
                  <li>Resolution: 2K</li>
                  <li>Format: PNG</li>
                </ul>
              </>
            )}
            className="ml-2 align-self-center"
            position="bottom"
          />
        </h4>
        <EventSelectorButton showFutureEvents />
        <UpdateButton label="Update Event" updateHandler={updateEventHandler} />
      </div>

      <div className={classNames('d-flex', 'flex-column', 'flex-grow-1', style.conceptPage)}>
        {isLoading && (
          <div className="flex-grow-1 d-flex justify-content-center align-items-center">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!isLoading && (
          <div className={style.conceptPageContent}>
            <div className={style.conceptPageColumns}>
              <div className={style.conceptPagePromptsCol}>
                <div className="mb-4">
                  <label className="form-label w-100" htmlFor="concept-title">
                    <span className="d-block">Title</span>
                    <input
                      id="concept-title"
                      type="text"
                      className="form-control mt-1"
                      value={conceptData?.title || ''}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      placeholder="What is this concept about?"
                    />
                  </label>
                </div>

                <ModelPrompts
                  value={conceptData?.models ?? {}}
                  onChange={(models) => setConceptData((prev) => ({ ...prev, models }))}
                />
              </div>

              <div className={style.conceptPageRightCol}>
                <PriorityOrder
                  value={priorityOrder}
                  onChange={setPriorityOrder}
                />
                <ImageUpload
                  slugDate={slugDate}
                  value={conceptData?.imageInputs || []}
                  onChange={(next) => setConceptData((prev) => ({
                    ...prev,
                    imageInputs: Array.isArray(next) ? next : [],
                  }))}
                />
              </div>
            </div>
          </div>
        )}

        <EventSelector singleSelection showTemplate showFutureEvents />
      </div>
    </div>
  );
};

export default connect(({ events: { selectedEvents, template, allEvents } }) => ({
  slugDate: selectedEvents[0]?.slugDate,
  template,
  allEvents,
}))(AiTemplateConcept);
